import React, { useState, useEffect, useRef } from 'react';
import { API_config } from '../../config/apiConfig';
// Using leaflet directly as it's assumed in this project (commonly used for map components here)
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CompareCars.styles.css';

const DEVICES_URL = 'https://admin.monitgps.pl/api/devices';

const CompareCars = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([{ id: '', from: '', to: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapData, setMapData] = useState([]); // [{ deviceId, positions: [...], events: [...] }]
  
  const [activeStampIndex, setActiveStampIndex] = useState(0);
  const [maxStamps, setMaxStamps] = useState(0);
  // Store an offset (in steps/seconds) for each loaded car
  const [offsets, setOffsets] = useState([0, 0, 0]);

  const mapRef = useRef(null);
  const shouldFitBoundsRef = useRef(true);
  const layersRef = useRef({
    lines: [],
    markers: []
  });

  const colors = ['#e63946', '#457b9d', '#2a9d8f'];

  useEffect(() => {
    // Fetch devices
    const fetchDevices = async () => {
      try {
        const res = await fetch(DEVICES_URL, {
          headers: {
            'Authorization': API_config.authHeader
          }
        });
        if (res.ok) {
          const data = await res.json();
          setDevices(data);
        } else {
          setError('Failed to fetch devices from Traccar.');
        }
      } catch (err) {
        setError('Error fetching devices.');
      }
    };
    fetchDevices();
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = L.map('compare-map').setView([52.2297, 21.0122], 6); // default center Poland
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapRef.current);
    }
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const clearMap = () => {
    layersRef.current.lines.forEach(l => l.remove());
    layersRef.current.markers.forEach(m => m.remove());
    layersRef.current.lines = [];
    layersRef.current.markers = [];
  };

  const drawOnMap = (data, index) => {
    if (!mapRef.current || !data) return;
    
    clearMap();
    
    let bounds = L.latLngBounds();
    let maxLen = 0;

    data.forEach((car, i) => {
      const color = colors[i % colors.length];
      const positions = car.positions || [];
      
      if (positions.length > maxLen) maxLen = positions.length;

      const latlngs = positions.map(p => {
        const lat = p.latitude ?? p.lat;
        const lon = p.longitude ?? p.lon;
        const point = [lat, lon];
        bounds.extend(point);
        return point;
      });

      if (latlngs.length > 0) {
        const polyline = L.polyline(latlngs, { color, weight: 4, opacity: 0.8 }).addTo(mapRef.current);
        layersRef.current.lines.push(polyline);
        
        // Add marker for the active stamp with the device's specific offset
        const deviceOffset = offsets[i] || 0;
        const currentStamp = Math.min(Math.max(0, index + deviceOffset), latlngs.length - 1);
        
        if (currentStamp >= 0 && currentStamp < positions.length) {
           const p = positions[currentStamp];
           const lat = p.latitude ?? p.lat;
           const lon = p.longitude ?? p.lon;
           // Prevent tooltips from overlapping when vehicles are in the exact same spot
           let direction = 'right';
           let offset = [10, 0];
           if (i % 3 === 0) { direction = 'top'; offset = [0, -10]; }
           else if (i % 3 === 1) { direction = 'bottom'; offset = [0, 10]; }

           const marker = L.circleMarker([lat, lon], {
              radius: 6,
              fillColor: color,
              color: '#fff',
              weight: 2,
              fillOpacity: 1
           });
           
           // Find matching alert for this position
           // We look for events that occurred around this position's time
           const positionTime = new Date(p.deviceTime).getTime();
           const carEvents = car.events || [];
           
           // Helper to determine status
           let statusLabel = 'Normal';
           let statusColor = color;
           
           // 1. Check position attributes (CarStatus, sos, alarm)
           const pAttr = p.attributes || {};
           if (pAttr.CarStatus === 'SOS' || pAttr.sos === true || pAttr.sos === 'true' || (typeof pAttr.alarm === 'string' && pAttr.alarm.toLowerCase().includes('sos'))) {
             statusLabel = 'SOS';
             statusColor = '#ff3b30'; // Premium Red
           } else if (pAttr.CarStatus === 'HAZARD' || (typeof pAttr.alarm === 'string' && pAttr.alarm.toLowerCase().includes('hazard'))) {
             statusLabel = 'HAZARD';
             statusColor = '#ff9500'; // Premium Orange
           } else if (pAttr.CarStatus === 'OK') {
             statusLabel = 'OK';
             statusColor = '#34c759'; // Premium Green
           }
           
           // 2. Overwrite with explicit Events if found (within 10s window)
           const activeEvent = carEvents.find(e => {
             const eventTime = new Date(e.eventTime || e.deviceTime).getTime();
             return Math.abs(eventTime - positionTime) < 10000; // 10s window
           });
           
           if (activeEvent) {
             const eAttr = activeEvent.attributes || {};
             const type = activeEvent.type;
             const alarm = eAttr.alarm || '';
             
             if (alarm.toLowerCase().includes('sos') || type === 'sos') {
               statusLabel = 'SOS';
               statusColor = '#ff3b30';
             } else if (alarm.toLowerCase().includes('hazard') || type === 'hazard') {
               statusLabel = 'HAZARD';
               statusColor = '#ff9500';
             } else if (alarm.toLowerCase().includes('ok') || type === 'ok') {
               statusLabel = 'OK';
               statusColor = '#34c759';
             }
           }

           if (statusLabel !== 'Normal') {
             marker.setStyle({ 
               radius: 9, 
               weight: 4, 
               color: '#fff', 
               fillColor: statusColor,
               fillOpacity: 1 
             });
           }

           marker.bindTooltip(
              `<div style="color: #ffffff; font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; min-width: 150px; padding: 2px;">
                <div style="font-size: 1.3em; font-weight: 900; border-bottom: 3px solid ${statusColor}; margin-bottom: 8px; padding-bottom: 4px; color: ${statusColor === '#34c759' ? '#7edb8c' : statusColor}">
                  STATUS: ${statusLabel.toUpperCase()}
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                  <span style="color: #a1a1b5;">Speed:</span>
                  <span style="font-weight: 700;">(${(p.speed * 1.852).toFixed(1)} km/h)</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #a1a1b5;">Time:</span>
                  <span style="font-weight: 700;">${new Date(p.deviceTime).toLocaleTimeString('en-GB')}</span>
                </div>
              </div>`, 
              { permanent: true, direction, offset, className: 'custom-compare-tooltip' }
           ).addTo(mapRef.current);
           layersRef.current.markers.push(marker);
        }
      }
    });

    if (maxLen > 0) {
      setMaxStamps(maxLen);
      if (bounds.isValid() && shouldFitBoundsRef.current) {
         mapRef.current.fitBounds(bounds, { padding: [20, 20] });
         shouldFitBoundsRef.current = false;
      }
    } else {
      setMaxStamps(0);
    }
  };

  // Re-draw markers when active index changes or offsets change
  useEffect(() => {
    if (mapData.length > 0) {
      drawOnMap(mapData, activeStampIndex);
    }
  }, [activeStampIndex, mapData, offsets]);

  const handleAddDevice = () => {
    if (selectedDevices.length < 3) {
      setSelectedDevices([...selectedDevices, { id: '', from: '', to: '' }]);
      setOffsets([...offsets, 0]);
    }
  };

  const handleRemoveDevice = (idx) => {
    const newSel = [...selectedDevices];
    newSel.splice(idx, 1);
    setSelectedDevices(newSel);

    const newOffsets = [...offsets];
    newOffsets.splice(idx, 1);
    setOffsets(newOffsets);
  };

  const handleDeviceChange = (idx, field, value) => {
    const newSel = [...selectedDevices];
    newSel[idx][field] = value;
    setSelectedDevices(newSel);
  };

  const handleLoadData = async () => {
    setLoading(true);
    setError(null);
    setMapData([]);
    setActiveStampIndex(0);
    setOffsets([0, 0, 0]);
    setMaxStamps(0);
    clearMap();

    // Filter valid selected devices
    const validReqs = selectedDevices.filter(d => d.id && d.from && d.to);
    
    if (validReqs.length === 0) {
      setError('Please select at least one device and set time ranges.');
      setLoading(false);
      return;
    }

    // Validate 20 minute limit
    for (const req of validReqs) {
      const fromMs = new Date(req.from).getTime();
      const toMs = new Date(req.to).getTime();
      const diffMins = (toMs - fromMs) / (1000 * 60);

      if (diffMins > 20) {
        setError(`Maximum time range per device is 20 minutes. Please adjust your range.`);
        setLoading(false);
        return;
      }
      if (diffMins <= 0) {
        setError(`'From' time must be earlier than 'To' time.`);
        setLoading(false);
        return;
      }
    }

    try {
      const newMapData = [];
      for (const req of validReqs) {
        // Traccar API requires ISO 8601 formats
        const isoFrom = new Date(req.from).toISOString();
        const isoTo = new Date(req.to).toISOString();
        const url = `${API_config.apiUrl}/positions?deviceId=${req.id}&from=${isoFrom}&to=${isoTo}`;
        
        const res = await fetch(url, {
          headers: {
            'Authorization': API_config.authHeader,
            'Accept': 'application/json'
          }
        });

        if (res.ok) {
          const positions = await res.json();
          
          // Also fetch events (alerts)
          let events = [];
          try {
            const eventsUrl = `${API_config.apiUrl}/reports/events?deviceId=${req.id}&from=${isoFrom}&to=${isoTo}`;
            const eRes = await fetch(eventsUrl, {
              headers: {
                'Authorization': API_config.authHeader,
                'Accept': 'application/json'
              }
            });
            if (eRes.ok) {
              events = await eRes.json();
            }
          } catch (eErr) {
            console.warn(`Failed to fetch events for device ${req.id}`, eErr);
          }

          newMapData.push({
            deviceId: req.id,
            positions: positions,
            events: events
          });
        } else {
          throw new Error(`API rejected request for device ${req.id}`);
        }
      }
      shouldFitBoundsRef.current = true;
      setMapData(newMapData);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error loading comparison data from Traccar API.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="compare-cars-container">
      <div className="compare-sidebar">
        <h3 className="compare-title">Compare Cars</h3>
        {error && <div className="compare-error">{error}</div>}
        
        {selectedDevices.map((d, idx) => (
          <div key={idx} className="compare-device-card" style={{ borderLeft: `4px solid ${colors[idx % colors.length]}` }}>
            <div className="compare-card-header">
              <span style={{ fontWeight: 600 }}>Car {idx + 1}</span>
              {selectedDevices.length > 1 && (
                <button className="compare-btn-icon" onClick={() => handleRemoveDevice(idx)} title="Remove">
                  <span className="material-icons-outlined">close</span>
                </button>
              )}
            </div>
            <div className="compare-form-group">
              <label>Device</label>
              <select value={d.id} onChange={(e) => handleDeviceChange(idx, 'id', e.target.value)}>
                <option value="">-- Select --</option>
                {devices.map(dev => (
                  <option key={dev.id} value={dev.id}>{dev.name} ({dev.id})</option>
                ))}
              </select>
            </div>
            <div className="compare-form-group">
              <label>From</label>
              <input type="datetime-local" value={d.from} onChange={(e) => handleDeviceChange(idx, 'from', e.target.value)} />
            </div>
            <div className="compare-form-group">
              <label>To</label>
              <input type="datetime-local" value={d.to} onChange={(e) => handleDeviceChange(idx, 'to', e.target.value)} />
            </div>
            
            {mapData.length > 0 && mapData[idx] && (
              <div className="compare-form-group">
                <label>Offset (+/- seconds)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="compare-btn-icon" onClick={() => {
                    const newOffsets = [...offsets];
                    newOffsets[idx] = (newOffsets[idx] || 0) - 1;
                    setOffsets(newOffsets);
                  }}><span className="material-icons-outlined">remove</span></button>
                  <input type="number" style={{ width: '80px', textAlign: 'center' }} value={offsets[idx] || 0} onChange={(e) => {
                    const newOffsets = [...offsets];
                    newOffsets[idx] = parseInt(e.target.value) || 0;
                    setOffsets(newOffsets);
                  }} />
                  <button className="compare-btn-icon" onClick={() => {
                    const newOffsets = [...offsets];
                    newOffsets[idx] = (newOffsets[idx] || 0) + 1;
                    setOffsets(newOffsets);
                  }}><span className="material-icons-outlined">add</span></button>
                </div>
              </div>
            )}
          </div>
        ))}

        {selectedDevices.length < 3 && (
          <button className="compare-btn compare-btn-secondary" onClick={handleAddDevice}>
            <span className="material-icons-outlined">add</span> Add Car
          </button>
        )}

        <button className="compare-btn compare-btn-primary" onClick={handleLoadData} disabled={loading}>
          {loading ? 'Loading...' : 'Load Data'}
        </button>

        {maxStamps > 0 && (
          <div className="compare-timeline-container">
            <h4>Timeline Analysis</h4>
            <input 
              type="range" 
              min={0} 
              max={maxStamps - 1} 
              value={activeStampIndex} 
              onChange={(e) => setActiveStampIndex(parseInt(e.target.value))}
              className="compare-slider"
            />
            <div className="compare-timeline-controls">
               <button onClick={() => setActiveStampIndex(Math.max(0, activeStampIndex - 1))} className="compare-btn-icon"><span className="material-icons-outlined">skip_previous</span></button>
               <span>Step {activeStampIndex + 1} / {maxStamps}</span>
               <button onClick={() => setActiveStampIndex(Math.min(maxStamps - 1, activeStampIndex + 1))} className="compare-btn-icon"><span className="material-icons-outlined">skip_next</span></button>
            </div>
          </div>
        )}
      </div>
      
      <div className="compare-map-wrapper">
        <div id="compare-map" style={{ width: '100%', height: '100%' }}></div>
      </div>
    </div>
  );
};

export default CompareCars;
